import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, Car, CircleDollarSign, ConciergeBell, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { RouteMap } from "@/components/map/RouteMap";
import { StatusBadges } from "@/components/StatusBadge";
import { useBooking, useCancelBooking } from "@/hooks/queries";
import { errorMessage } from "@/lib/api";
import { SERVICE_LABELS, BOOKING_PHONE } from "@/lib/booking";
import { PAYMENT_LABELS, fareLabel, fmtDateTime, isUpcoming, money } from "@/lib/format";

const Detail = ({ icon: Icon, label, value, testId }) => (
  <div className="lux-card p-4">
    <Icon className="h-4 w-4 text-gold" strokeWidth={1.6} />
    <p className="eyebrow mt-3 text-[10px]">{label}</p>
    <p className="mt-1 text-sm text-ivory" data-testid={testId}>{value}</p>
  </div>
);

const Stop = ({ kind, label, address }) => (
  <div className="flex gap-3">
    <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${kind === "pickup" ? "bg-gold" : "border-2 border-gold bg-ivory"}`} />
    <div>
      <p className="eyebrow text-[10px]">{label}</p>
      <p className="mt-1 text-sm leading-relaxed text-ivory" data-testid={`trip-${kind}-address`}>{address}</p>
    </div>
  </div>
);

function CancelButton({ id }) {
  const cancel = useCancelBooking();
  const run = () =>
    cancel.mutate(id, {
      onSuccess: () => toast.success("Your booking has been cancelled"),
      onError: (e) => toast.error(errorMessage(e)),
    });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" className="btn-ghost text-cancelled hover:border-cancelled/50" data-testid="cancel-booking-btn">
          {cancel.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Cancel booking
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-3xl border-line bg-surface">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">Cancel this ride?</AlertDialogTitle>
          <AlertDialogDescription className="text-mist">Your chauffeur will be released. This can't be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="cancel-booking-dismiss-btn">Keep ride</AlertDialogCancel>
          <AlertDialogAction onClick={run} className="rounded-full bg-cancelled text-ink hover:bg-cancelled/90" data-testid="cancel-booking-confirm-btn">
            Cancel ride
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function TripDetail() {
  const { id } = useParams();
  const { data: b, isLoading, isError } = useBooking(id);

  if (isLoading) return <div className="h-96 animate-pulse rounded-3xl bg-surface" data-testid="trip-detail-loading" />;
  if (isError || !b)
    return (
      <div className="lux-card p-8 text-center" data-testid="trip-detail-error">
        <p className="text-mist">We couldn't find this trip.</p>
        <Link to="/trips" className="btn-ghost mt-4">Back to trips</Link>
      </div>
    );

  const quotePending = b.quote_status === "pending";
  const payable = b.status === "confirmed" && b.payment_status !== "paid" && !quotePending;
  return (
    <div className="space-y-6" data-testid="trip-detail-page">
      <Link to="/trips" className="inline-flex items-center gap-2 text-sm text-mist hover:text-gold" data-testid="trip-back-link">
        <ArrowLeft className="h-4 w-4" /> All trips
      </Link>
      <div className="rise flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-gold">{SERVICE_LABELS[b.service_type]}</p>
          <h1 className="mt-2 text-3xl tracking-tight text-ivory sm:text-4xl lg:text-5xl" data-testid="trip-datetime">{fmtDateTime(b.scheduled_at)}</h1>
        </div>
        <StatusBadges booking={b} testId="trip-detail" />
      </div>

      <div className="rise stagger-1">
        <RouteMap pickup={b.pickup} dropoff={b.dropoff} />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
        <div className="lux-card rise stagger-2 space-y-5 p-6">
          <Stop kind="pickup" label="Pickup" address={b.pickup.address} />
          <div className="ml-[4px] h-6 border-l border-dashed border-gold/40" />
          {b.dropoff ? <Stop kind="dropoff" label="Drop-off" address={b.dropoff.address} /> : <Stop kind="dropoff" label="Itinerary" address={`${b.hours} hours, as directed by you`} />}
          {b.notes && (
            <div className="rounded-2xl border border-line bg-elevated/50 p-4">
              <p className="eyebrow text-[10px]">Notes</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-mist" data-testid="trip-notes">{b.notes}</p>
            </div>
          )}
        </div>

        <div className="rise stagger-3 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Detail icon={Car} label="Vehicle" value={`${b.vehicle_name} · ${b.vehicle_model}`} testId="trip-vehicle" />
            <Detail icon={Users} label="Passengers" value={b.passengers} testId="trip-passengers" />
            <Detail icon={CalendarClock} label={b.hours ? "Duration" : "Distance"} value={b.hours ? `${b.hours} hours` : `~${b.distance_miles} mi`} testId="trip-duration" />
            <Detail
              icon={CircleDollarSign}
              label="Payment"
              value={b.payment_status === "paid" ? `Paid · ${PAYMENT_LABELS[b.payment_method]}${b.payment_verification === "verified" ? " · Verified" : ""}` : b.payment_status === "pending" ? `Pending · ${PAYMENT_LABELS[b.payment_method]}` : "Unpaid"}
              testId="trip-payment-status"
            />
          </div>
          {b.payment_status === "unpaid" && b.payment_verification === "not_received" && b.status === "confirmed" && (
            <div className="rounded-2xl border border-cancelled/30 bg-cancelled/10 p-4 text-sm text-ivory" data-testid="trip-payment-not-received-notice">
              We couldn't confirm your last payment. Please pay again below, or message your concierge if you believe this is a mistake.
            </div>
          )}
          {quotePending && b.status === "confirmed" && (
            <div className="rounded-2xl border border-gold/30 bg-gold/10 p-4 text-sm text-ivory" data-testid="trip-quote-pending-notice">
              Luxury Elite ATL is confirming your rate for this trip. Payment opens once it's set. Questions? Call{" "}
              <a href={BOOKING_PHONE.tel} className="text-gold underline">{BOOKING_PHONE.display}</a>.
            </div>
          )}
          <div className="lux-card flex items-center justify-between p-5">
            <div>
              <p className="eyebrow text-[10px]">{quotePending ? "Rate" : "Total fare"}</p>
              <p className="font-display text-3xl text-gold" data-testid="trip-fare">{fareLabel(b)}</p>
            </div>
            {payable && (
              <Link to={`/trips/${b.id}/pay`} className="btn-gold" data-testid="pay-booking-btn">Pay {money(b.fare)}</Link>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/concierge" className="btn-ghost" data-testid="trip-concierge-link">
              <ConciergeBell className="h-4 w-4 text-gold" /> Ask your concierge
            </Link>
            {isUpcoming(b) && <CancelButton id={b.id} />}
          </div>
        </div>
      </div>
    </div>
  );
}
